import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Crosshair, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLocation } from "@/context/LocationContext";
import { cityCoords } from "@/lib/places";

const PIN = L.divIcon({
  className: "",
  html: '<div style="width:30px;height:30px;border-radius:50% 50% 50% 0;background:#10b981;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 3px 8px rgba(0,0,0,.35)"></div>',
  iconSize: [30, 30],
  iconAnchor: [15, 30],
});

export function LocationPicker({ coords, setCoords, country, city, address, testid = "loc" }) {
  const box = useRef(null);
  const mapRef = useRef(null);
  const markRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const { requestGPS } = useLocation();

  const place = (c, zoom) => {
    const map = mapRef.current;
    if (!map) return;
    const ll = [c.lat, c.lng];
    if (!markRef.current) {
      markRef.current = L.marker(ll, { icon: PIN, draggable: true }).addTo(map);
      markRef.current.on("dragend", () => { const p = markRef.current.getLatLng(); setCoords({ lat: p.lat, lng: p.lng }); });
    } else markRef.current.setLatLng(ll);
    map.setView(ll, zoom || Math.max(map.getZoom(), 16));
  };

  useEffect(() => {
    if (!box.current || mapRef.current) return;
    const start = coords || cityCoords(country, city);
    const map = L.map(box.current, { zoomControl: true, attributionControl: false }).setView(start ? [start.lat, start.lng] : [56.67, 12.86], start ? (coords ? 16 : 12) : 5);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
    map.on("click", (e) => { setCoords({ lat: e.latlng.lat, lng: e.latlng.lng }); place({ lat: e.latlng.lat, lng: e.latlng.lng }); });
    mapRef.current = map;
    if (coords) place(coords, 16);
    return () => { map.remove(); mapRef.current = null; markRef.current = null; };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (coords || !mapRef.current) return;
    const c = cityCoords(country, city);
    if (c) mapRef.current.setView([c.lat, c.lng], 12);
  }, [country, city, coords]);

  const gps = async () => {
    try { const c = await requestGPS(); setCoords({ lat: c.lat, lng: c.lng }); place(c, 17); toast.success("Vendndodhja u ruajt"); }
    catch { toast.error("Nuk u mor vendndodhja"); }
  };

  const fromAddress = async () => {
    if (!address?.trim() || !city) return toast.error("Shkruaj adresën dhe zgjidh qytetin");
    setBusy(true);
    try {
      const near = cityCoords(country, city);
      const { data } = await api.get("/geocode", { params: { q: `${address}, ${city}`, country, ...(near || {}) } });
      const c = { lat: data.coords[0], lng: data.coords[1] };
      setCoords(c);
      place(c, 17);
      toast.success("Marker-i u vendos. Lëvize me gisht nëse duhet.");
    } catch { toast.error("Adresa nuk u gjet. Prek hartën për ta vendosur vetë."); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-3 bg-slate-50 rounded-xl border border-slate-200 p-3" data-testid={`${testid}-picker`}>
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2"><MapPin className="w-4 h-4 text-emerald-500" />Vendndodhja në hartë</div>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" data-testid={`${testid}-gps`} onClick={gps} className="flex items-center justify-center gap-1.5 bg-emerald-500 text-white rounded-xl h-11 text-sm font-semibold active:scale-95 transition-transform"><Crosshair className="w-4 h-4" />Vendndodhja ime</button>
        <button type="button" data-testid={`${testid}-from-address`} onClick={fromAddress} disabled={busy} className="flex items-center justify-center gap-1.5 bg-white border border-emerald-500 text-emerald-600 rounded-xl h-11 text-sm font-semibold active:scale-95 transition-transform">{busy ? "Duke kërkuar..." : "Gjej nga adresa"}</button>
      </div>
      <div ref={box} data-testid={`${testid}-map`} className="mt-2 rounded-xl overflow-hidden border border-slate-200" style={{ height: 220, zIndex: 0 }} />
      <p className="text-[11px] text-slate-500 mt-1.5 text-center">Prek hartën ose zvarrit marker-in me gisht për ta vendosur saktë.</p>
      {coords && <p data-testid={`${testid}-coords`} className="text-xs text-emerald-600 mt-1 text-center">✓ Koordinatat u ruajtën ({coords.lat.toFixed(5)}, {coords.lng.toFixed(5)})</p>}
    </div>
  );
}
